import { useState, useEffect, useMemo, useCallback } from 'react';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Input, Checkbox, message, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { dashboardReportApi, brandApi, templateApi, scheduleApi, permissionConfigApi, milestoneNodeApi } from '../api/services';
import type {
  DashboardReportProjectVO,
  DashboardReportResultVO,
  MilestoneTemplateVO,
  UserOptionVO,
} from '../api/types';
import dayjs from 'dayjs';
import { projectStatusLabel, milestoneStatusLabel, projectStatusOptions, milestoneStatusOptions } from '../utils/ipcStatusEnums';
import { useIpcStatusEnums } from '../utils/useIpcStatusEnums';
import {
  IpcButton,
  IpcFilterToolbar,
  IpcModal,
  IpcNodeStatus,
  IpcNodeStatusPicker,
  IpcStatusBadge,
  IpcTable,
  IpcTag,
  IpcEditableDate,
  IpcDateRangePicker,
  IpcRichTextEditor,
  IpcRichTextView,
  IPC_FILTER_MULTI_SELECT_PROPS,
} from '../components/ui';
import { normalizeNodeProgress } from '../utils/nodeStatus';
import { normalizeNodeVisualStatus } from '../utils/ipcStatusVisual';
import { isRichTextEmpty, normalizeRichTextForSave } from '../utils/richText';
import { ProjectCodeDisplay, SpecSkuDisplay } from '../components/ProjectCodeDisplay';
import { useListViewQuery } from '../hooks/useListViewQuery';
import { LIST_VIEW_KEYS } from '../utils/listViewSession';
import './DashboardReport.css';
import './Dashboard.css';

type ReportFilters = {
  keyword: string;
  brands: string[];
  templateIds: number[];
  projectStatuses: string[];
  milestoneStatuses: string[];
  creators: string[];
  planEndStart: string | null;
  planEndEnd: string | null;
};

/** 重置：恢复页面默认筛选（进行中 + 计划结束±7 天等） */
function createDefaultReportFilters(): ReportFilters {
  const range = createDefaultPlanEndRange();
  return {
    keyword: '',
    brands: [],
    templateIds: [],
    projectStatuses: ['in_progress'],
    milestoneStatuses: ['in_progress'],
    creators: [],
    planEndStart: range.start.format('YYYY-MM-DD'),
    planEndEnd: range.end.format('YYYY-MM-DD'),
  };
}

/** 清除全部：所有条件置空 */
const EMPTY_REPORT_FILTERS: ReportFilters = {
  keyword: '',
  brands: [],
  templateIds: [],
  projectStatuses: [],
  milestoneStatuses: [],
  creators: [],
  planEndStart: null,
  planEndEnd: null,
};

function createDefaultPlanEndRange(): { start: Dayjs; end: Dayjs } {
  return {
    start: dayjs().subtract(7, 'day').startOf('day'),
    end: dayjs().add(7, 'day').startOf('day'),
  };
}

/* ─── 异常判定 ─── */
interface AnomalyInfo {
  type: 'overdue_not_started' | 'delayed';
  label: string;
  nodeName?: string;
}

function calcAnomaly(ms: { milestoneStatus?: string; planStartDate?: string; planEndDate?: string }): AnomalyInfo | null {
  const now = dayjs().startOf('day');
  const status = (ms.milestoneStatus ?? '').trim().toLowerCase() || 'pending';
  if (status === 'terminated' || status === 'completed') return null;
  const planStart = ms.planStartDate ? dayjs(ms.planStartDate).startOf('day') : null;
  const planEnd = ms.planEndDate ? dayjs(ms.planEndDate).startOf('day') : null;

  // 逾期待开始：计划开始时间已到但状态仍为待开始
  if (planStart && planStart.isValid() && !planStart.isAfter(now) && status === 'pending') {
    return { type: 'overdue_not_started', label: '已超时' };
  }
  // 已超时：计划结束时间已过但未完成
  if (planEnd && planEnd.isValid() && planEnd.isBefore(now) && status !== 'completed') {
    return { type: 'delayed', label: '已超时' };
  }
  return null;
}

/* ─── 原有类型定义（保持不变） ─── */

type RowType = 'project-header' | 'node';

interface NodeRowData {
  projectId: number;
  scheduleId?: number | null;
  scheduleVersion?: number | null;
  scheduleStatus?: string;
  specScheduleId: number;
  specName: string;
  /** 规格状态；已终止时节点只读 */
  specStatus?: string;
  skuCodes?: string;
  isMerged: boolean;
  milestoneId: number;
  milestoneName: string;
  milestoneStatus: string;
  planStartDate?: string;
  planEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  note?: string;
  coverageLabel?: string;
  anomaly?: AnomalyInfo | null;   // ★ 新增
}

interface ReportTableRow {
  key: string;
  rowType: RowType;
  projectId: number;
  projectName?: string;
  projectCode?: string;
  ipdProjectCodes?: string[];
  brand?: string;
  templateName?: string;
  expectedLaunchDate?: string;
  progressSync?: string;
  scheduleId?: number | null;
  scheduleVersion?: number | null;
  scheduleStatus?: string;
  specCount?: number;
  node?: NodeRowData;
  creator?: string;               // ★ 新增：创建人名称
}

/* ─── 原有辅助函数（保持不变） ─── */

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : '—';
}

function pickField<T>(obj: Record<string, unknown>, keys: string[]): T | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && v !== '') return v as T;
  }
  return undefined;
}

function normalizeProject(raw: Record<string, unknown>): DashboardReportProjectVO {
  const specs = (raw.specs ?? raw.Specs ?? []) as Record<string, unknown>[];
  const ipdRaw = raw.ipdProjectCodes ?? raw.IpdProjectCodes;
  const ipdProjectCodes = Array.isArray(ipdRaw)
    ? ipdRaw.map(c => String(c ?? '').trim()).filter(Boolean)
    : [];
  return {
    projectId: Number(raw.projectId ?? raw.ProjectId ?? 0),
    projectCode: String(raw.projectCode ?? raw.ProjectCode ?? ''),
    ipdProjectCodes,
    projectName: String(raw.projectName ?? raw.ProjectName ?? ''),
    brand: String(raw.brand ?? raw.Brand ?? ''),
    projectStatus: String(raw.projectStatus ?? raw.ProjectStatus ?? ''),
    projectStatusLabel: String(raw.projectStatusLabel ?? raw.ProjectStatusLabel ?? ''),
    templateId: pickField<number>(raw, ['templateId', 'TemplateId']) ?? null,
    templateName: String(raw.templateName ?? raw.TemplateName ?? ''),
    progressSync: String(raw.progressSync ?? raw.ProgressSync ?? ''),
    scheduleId: pickField<number>(raw, ['scheduleId', 'ScheduleId']) ?? null,
    scheduleVersion: pickField<number>(raw, ['scheduleVersion', 'ScheduleVersion']) ?? null,
    scheduleStatus: String(raw.scheduleStatus ?? raw.ScheduleStatus ?? ''),
    scheduleName: String(raw.scheduleName ?? raw.ScheduleName ?? ''),
    expectedLaunchDate: String(raw.expectedLaunchDate ?? raw.ExpectedLaunchDate ?? ''),
    updateTime: String(raw.updateTime ?? raw.UpdateTime ?? ''),
    creator: String(raw.creator ?? raw.Creator ?? raw.CreatorName ?? raw.creatorName ?? ''),   // ★ 新增
    specs: specs.map(sp => {
      const milestones = (sp.milestones ?? sp.Milestones ?? []) as Record<string, unknown>[];
      return {
        specScheduleId: Number(sp.specScheduleId ?? sp.SpecScheduleId ?? 0),
        specName: String(sp.specName ?? sp.SpecName ?? ''),
        specStatus: String(sp.specStatus ?? sp.SpecStatus ?? ''),
        skuCodes: String(sp.skuCodes ?? sp.SkuCodes ?? ''),
        skuCount: (() => {
          const raw = sp.skuCount ?? sp.SkuCount;
          if (raw == null || raw === '') return undefined;
          const n = Number(raw);
          return Number.isFinite(n) ? n : undefined;
        })(),
        milestones: milestones.map(ms => ({
          milestoneId: Number(ms.milestoneId ?? ms.MilestoneId ?? 0),
          milestoneName: String(ms.milestoneName ?? ms.MilestoneName ?? ''),
          status: String(ms.status ?? ms.Status ?? ''),
          planStartDate: pickField<string>(ms, ['planStartDate', 'PlanStartDate']),
          planEndDate: pickField<string>(ms, ['planEndDate', 'PlanEndDate']),
          actualStartDate: pickField<string>(ms, ['actualStartDate', 'ActualStartDate']),
          actualEndDate: pickField<string>(ms, ['actualEndDate', 'ActualEndDate']),
          note: String(ms.note ?? ms.Note ?? ''),
          mergeAcrossSpecs: !!(ms.mergeAcrossSpecs ?? ms.MergeAcrossSpecs),
          milestoneOrder: Number(ms.milestoneOrder ?? ms.MilestoneOrder ?? 0),
        })),
      };
    }),
  };
}

function countLinkedSkus(specs: DashboardReportProjectVO['specs'] | undefined): number {
  let total = 0;
  for (const spec of specs ?? []) {
    const codesLen = (spec.skuCodes ?? '')
      .split(' - ')
      .map(c => c.trim())
      .filter(Boolean).length;
    const apiCount = typeof spec.skuCount === 'number' && Number.isFinite(spec.skuCount)
      ? Math.max(0, spec.skuCount)
      : 0;
    // 优先用接口关联数；若接口未返回/为 0 但有展示编码，至少按编码数兜底
    total += Math.max(apiCount, codesLen);
  }
  return total;
}

function buildTableRows(projects: DashboardReportProjectVO[]): ReportTableRow[] {
  const rows: ReportTableRow[] = [];
  for (const proj of projects) {
    const specCount = countLinkedSkus(proj.specs);
    const baseKey = String(proj.projectId);
    const specNames = (proj.specs ?? []).map(s => s.specName).filter(Boolean);
    const coverageLabel = specNames.join('/');

    rows.push({
      key: `${baseKey}-header`,
      rowType: 'project-header',
      projectId: proj.projectId,
      projectName: proj.projectName,
      projectCode: proj.projectCode,
      ipdProjectCodes: proj.ipdProjectCodes,
      brand: proj.brand,
      templateName: proj.templateName,
      expectedLaunchDate: proj.expectedLaunchDate,
      progressSync: proj.progressSync,
      scheduleId: proj.scheduleId,
      scheduleVersion: proj.scheduleVersion,
      scheduleStatus: proj.scheduleStatus,
      specCount,
      creator: proj.creator || '',   // ★ 新增
    });

    const seenMerged = new Set<number>();
    const nodeItems: Array<{ order: number; node: NodeRowData }> = [];

    for (const spec of proj.specs ?? []) {
      for (const ms of spec.milestones ?? []) {
        const isMerged = !!ms.mergeAcrossSpecs;
        if (isMerged) {
          if (seenMerged.has(ms.milestoneId)) continue;
          seenMerged.add(ms.milestoneId);
        }
        const anomaly = calcAnomaly({   // ★ 新增：异常判定
          milestoneStatus: ms.status,
          planStartDate: ms.planStartDate,
          planEndDate: ms.planEndDate,
        });
        nodeItems.push({
          order: ms.milestoneOrder ?? 0,
          node: {
            projectId: proj.projectId,
            scheduleId: proj.scheduleId,
            scheduleVersion: proj.scheduleVersion,
            scheduleStatus: proj.scheduleStatus,
            specScheduleId: spec.specScheduleId,
            specName: isMerged ? '共用' : spec.specName,
            specStatus: spec.specStatus,
            skuCodes: isMerged ? undefined : spec.skuCodes,
            isMerged,
            milestoneId: ms.milestoneId,
            milestoneName: ms.milestoneName,
            coverageLabel: isMerged ? coverageLabel : undefined,
            milestoneStatus: ms.status,
            planStartDate: ms.planStartDate,
            planEndDate: ms.planEndDate,
            actualStartDate: ms.actualStartDate,
            actualEndDate: ms.actualEndDate,
            note: ms.note,
            anomaly,   // ★ 新增
          },
        });
      }
    }

    nodeItems.sort((a, b) => {
      if (a.node.isMerged !== b.node.isMerged) return a.node.isMerged ? -1 : 1;
      if (a.order !== b.order) return a.order - b.order;
      return a.node.milestoneId - b.node.milestoneId;
    });

    for (const item of nodeItems) {
      rows.push({
        key: `${baseKey}-node-${item.node.specScheduleId}-${item.node.milestoneId}`,
        rowType: 'node',
        projectId: proj.projectId,
        node: item.node,
      });
    }
  }
  return rows;
}

/* ─── 原有 EditableCell 组件 ─── */

interface EditableDateCellProps {
  value?: string;
  saving: boolean;
  onSave: (value: string) => void;
}

const EditableDateCell: FC<EditableDateCellProps> = ({ value, saving, onSave }) => (
  <IpcEditableDate
    value={value}
    saving={saving}
    format="YYYY-MM-DD"
    onSave={next => onSave(next ?? '')}
  />
);

interface EditableTextCellProps {
  value?: string;
  saving: boolean;
  onSave: (value: string) => void;
  /** 多行/备注类字段：轻量富文本 */
  multiline?: boolean;
  richVariant?: 'inline' | 'block';
  placeholder?: string;
}

const EditableTextCell: FC<EditableTextCellProps> = ({
  value,
  saving,
  onSave,
  multiline,
  richVariant = 'inline',
  placeholder = '请输入…',
}) => {
  const [editing, setEditing] = useState(false);

  if (multiline) {
    if (!editing) {
      return (
        <span
          className={`editable-cell editable-cell--text editable-cell--rich${saving ? ' editable-cell--saving' : ''}${isRichTextEmpty(value) ? ' is-empty' : ''}`}
          onClick={() => !saving && setEditing(true)}
        >
          <IpcRichTextView value={value} emptyText="—" />
        </span>
      );
    }
    return (
      <IpcRichTextEditor
        variant={richVariant}
        className="report-note-editor"
        value={value}
        placeholder={placeholder}
        autoFocus
        disabled={saving}
        onBlur={html => {
          setEditing(false);
          onSave(html);
        }}
      />
    );
  }

  const display = (value ?? '').trim() || '—';
  if (!editing) {
    return (
      <span
        className={`editable-cell editable-cell--text${saving ? ' editable-cell--saving' : ''}`}
        onClick={() => !saving && setEditing(true)}
      >
        {display}
      </span>
    );
  }

  return (
    <Input
      size="small"
      className="report-inline-editor"
      autoFocus
      defaultValue={value ?? ''}
      disabled={saving}
      onBlur={e => {
        setEditing(false);
        onSave(e.currentTarget.value);
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  );
};

function resolveReportNodeStatusProgress(value?: string | null) {
  return normalizeNodeVisualStatus(value);
}

// ★ 扩展：状态单元格支持异常标注
interface EditableStatusCellProps {
  value: string;
  saving: boolean;
  anomaly?: AnomalyInfo | null;
  /** 自动完成节点不可手动选「已完成」 */
  excludeCompleted?: boolean;
  onSave: (value: string) => void;
}

const EditableStatusCell: FC<EditableStatusCellProps> = ({
  value,
  saving,
  anomaly,
  excludeCompleted,
  onSave,
}) => {
  const raw = (value || '').trim().toLowerCase();
  // 编辑器仅三态；已终止单独只读展示，避免被归一成「待开始」
  if (raw === 'terminated') {
    return (
      <span className="editable-cell editable-cell--status">
        <IpcNodeStatus progress="terminated" compact />
      </span>
    );
  }

  const progress = normalizeNodeProgress(value);
  const time = anomaly ? 'delayed' as const : 'normal' as const;

  const cell = (
    <IpcNodeStatusPicker
      progress={progress}
      time={time}
      saving={saving}
      excludeProgress={excludeCompleted ? ['completed'] : undefined}
      onChange={next => {
        if (excludeCompleted && next === 'completed') {
          message.warning('自动完成节点不可手动改为已完成');
          return;
        }
        onSave(next);
      }}
    />
  );

  if (anomaly) {
    return (
      <Tooltip title={anomaly.label} placement="top">
        <span className="editable-cell editable-cell--status">{cell}</span>
      </Tooltip>
    );
  }

  return <span className="editable-cell editable-cell--status">{cell}</span>;
};

/* ─── 主组件 ─── */

const DashboardReport: FC = () => {
  useIpcStatusEnums();

  const navigate = useNavigate();
  const [result, setResult] = useState<DashboardReportResultVO | null>(null);
  const [loading, setLoading] = useState(false);
  const [brandOptions, setBrandOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [templateOptions, setTemplateOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [creatorUsers, setCreatorUsers] = useState<UserOptionVO[]>([]);
  const {
    draft: draftFilters,
    setDraft: setDraftFilters,
    applied: appliedFilters,
    reset: resetReportQuery,
    clearAll: clearReportQuery,
    patchApplied: patchReportApplied,
    applyIfChanged: applyReportIfChanged,
    patchAndApply: patchAndApplyReport,
    page: pageIndex,
    pageSize,
    setPagination: setReportPagination,
  } = useListViewQuery<ReportFilters>({
    listViewKey: LIST_VIEW_KEYS.dashboardReport,
    defaultFilters: createDefaultReportFilters,
    emptyFilters: EMPTY_REPORT_FILTERS,
  });
  const filterCreators = appliedFilters.creators;
  const filterPlanEndStart = appliedFilters.planEndStart ? dayjs(appliedFilters.planEndStart) : null;
  const filterPlanEndEnd = appliedFilters.planEndEnd ? dayjs(appliedFilters.planEndEnd) : null;
  // ★ 批量发布
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [savingKey, setSavingKey] = useState<string>('');
  const [autoCompletionNodeIds, setAutoCompletionNodeIds] = useState<Set<number>>(() => new Set());

  // ★ 初始化：加载品牌、模板、创建人候选（mutable-users + action_flag=1）
  useEffect(() => {
    brandApi.getList().then(options => setBrandOptions(options.map(b => ({ value: b.code, label: b.name })))).catch(() => {});
    templateApi.getList({ pageNum: 1, pageSize: 200 }).then(res => {
      setTemplateOptions((res.list ?? []).map((t: MilestoneTemplateVO) => ({ value: t.id, label: t.name ?? '' })));
    }).catch(() => {});
    permissionConfigApi.getMutableUsers()
      .then(users => setCreatorUsers(users ?? []))
      .catch(() => setCreatorUsers([]));
    milestoneNodeApi.getList({ pageNum: 1, pageSize: 9999 }).then(res => {
      const ids = new Set<number>();
      for (const n of res?.list ?? []) {
        const method = (n.completion_method ?? n.completionMethod ?? 'manual')
          .toString()
          .trim()
          .toLowerCase();
        if (method === 'auto' && Number(n.id) > 0) ids.add(Number(n.id));
      }
      setAutoCompletionNodeIds(ids);
    }).catch(() => setAutoCompletionNodeIds(new Set()));
  }, []);

  const brandLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    brandOptions.forEach(option => { map[option.value] = option.label; });
    return map;
  }, [brandOptions]);

  const fetchReport = useCallback(async (filters: ReportFilters = appliedFilters) => {
    setLoading(true);
    try {
      const data = await dashboardReportApi.getReport({
        brand: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        templateIds: filters.templateIds.length > 0 ? filters.templateIds.join(',') : undefined,
        projectStatus: filters.projectStatuses.length > 0 ? filters.projectStatuses.join(',') : undefined,
        milestoneStatus: filters.milestoneStatuses.length > 0 ? filters.milestoneStatuses.join(',') : undefined,
        keyword: filters.keyword || undefined,
      });
      const raw = data as unknown as Record<string, unknown>;
      const projectsRaw = (raw.projects ?? raw.Projects ?? []) as Record<string, unknown>[];
      setResult({ projects: projectsRaw.map(normalizeProject) });
    } catch (e: unknown) {
      message.error('加载项目汇报失败: ' + (e instanceof Error ? e.message : ''));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    void fetchReport(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 首屏按会话 hydrate
  }, []);

  const fetchReportIfApplied = (coords: { applied: ReportFilters } | null) => {
    if (coords) void fetchReport(coords.applied);
  };

  const applyReportFilters = () => {
    fetchReportIfApplied(applyReportIfChanged({
      ...draftFilters,
      keyword: draftFilters.keyword.trim(),
    }));
  };

  /** 重置：恢复默认筛选并立即请求 */
  const resetReportFiltersToDefault = () => {
    setSelectedProjectIds([]);
    fetchReportIfApplied(resetReportQuery());
  };

  /** 清除全部：清空所有条件（不含默认值）并立即请求 */
  const clearAllReportFilters = () => {
    setSelectedProjectIds([]);
    fetchReportIfApplied(clearReportQuery());
  };

  const reportFilterChips = [
    appliedFilters.keyword.trim()
      ? { key: 'keyword', label: `关键词: ${appliedFilters.keyword.trim()}` }
      : null,
    ...appliedFilters.brands.map(code => ({
      key: `brand:${code}`,
      label: `品牌: ${brandLabelMap[code] ?? code}`,
    })),
    ...appliedFilters.templateIds.map(id => ({
      key: `template:${id}`,
      label: `模板: ${templateOptions.find(t => t.value === id)?.label ?? id}`,
    })),
    ...filterCreators.map(name => ({
      key: `creator:${name}`,
      label: `创建人: ${name}`,
    })),
    filterPlanEndStart || filterPlanEndEnd
      ? {
          key: 'planEnd',
          label: `计划结束: ${filterPlanEndStart?.format('YYYY-MM-DD') ?? '…'} ~ ${filterPlanEndEnd?.format('YYYY-MM-DD') ?? '…'}`,
        }
      : null,
    ...appliedFilters.projectStatuses.map(status => ({
      key: `projectStatus:${status}`,
      label: `项目状态: ${projectStatusLabel(status)}`,
    })),
    ...appliedFilters.milestoneStatuses.map(status => ({
      key: `milestoneStatus:${status}`,
      label: `节点状态: ${milestoneStatusLabel(status)}`,
    })),
  ].filter((item): item is { key: string; label: string } => item != null);

  const removeReportAppliedChip = (key: string) => {
    if (key === 'keyword') {
      fetchReportIfApplied(patchReportApplied({ keyword: '' }));
      return;
    }
    if (key.startsWith('brand:')) {
      const code = key.slice('brand:'.length);
      fetchReportIfApplied(patchReportApplied({
        brands: appliedFilters.brands.filter(b => b !== code),
      }));
      return;
    }
    if (key.startsWith('template:')) {
      const id = Number(key.slice('template:'.length));
      fetchReportIfApplied(patchReportApplied({
        templateIds: appliedFilters.templateIds.filter(t => t !== id),
      }));
      return;
    }
    if (key.startsWith('creator:')) {
      const name = key.slice('creator:'.length);
      patchReportApplied({ creators: appliedFilters.creators.filter(c => c !== name) });
      return;
    }
    if (key === 'planEnd') {
      patchReportApplied({ planEndStart: null, planEndEnd: null });
      return;
    }
    if (key.startsWith('projectStatus:')) {
      const status = key.slice('projectStatus:'.length);
      fetchReportIfApplied(patchReportApplied({
        projectStatuses: appliedFilters.projectStatuses.filter(s => s !== status),
      }));
      return;
    }
    if (key.startsWith('milestoneStatus:')) {
      const status = key.slice('milestoneStatus:'.length);
      fetchReportIfApplied(patchReportApplied({
        milestoneStatuses: appliedFilters.milestoneStatuses.filter(s => s !== status),
      }));
    }
  };

  // ★ 创建人选项：PermissionConfig/mutable-users（后端已过滤 action_flag=1）
  const creatorOptions = useMemo(() => {
    return creatorUsers.map(user => ({
      value: user.user_name,
      label: user.user_name || '未知用户',
    }));
  }, [creatorUsers]);

  // ★ 客户端筛选：创建人（OR）+ 计划结束范围；创建人与计划结束为 AND
  // 计划结束：只保留符合条件的节点（非整项目保留）
  const filteredProjects = useMemo(() => {
    let projects = result?.projects ?? [];

    if (filterCreators.length > 0) {
      projects = projects.filter(p => {
        const creatorName = (p.creator ?? '').trim();
        if (!creatorName) return false;
        return filterCreators.includes(creatorName);
      });
    }

    if (filterPlanEndStart || filterPlanEndEnd) {
      const start = filterPlanEndStart ? filterPlanEndStart.startOf('day') : null;
      const end = filterPlanEndEnd ? filterPlanEndEnd.endOf('day') : null;
      const inPlanEndRange = (planEndDate?: string) => {
        if (!planEndDate) return false;
        const d = dayjs(planEndDate);
        if (!d.isValid()) return false;
        if (start && d.isBefore(start)) return false;
        if (end && d.isAfter(end)) return false;
        return true;
      };
      projects = projects
        .map(p => ({
          ...p,
          specs: (p.specs ?? [])
            .map(spec => ({
              ...spec,
              milestones: (spec.milestones ?? []).filter(ms => inPlanEndRange(ms.planEndDate)),
            }))
            .filter(spec => (spec.milestones?.length ?? 0) > 0),
        }))
        .filter(p => (p.specs?.length ?? 0) > 0);
    }

    return projects;
  }, [result, filterCreators, filterPlanEndStart, filterPlanEndEnd]);

  const totalProjects = filteredProjects.length;

  useEffect(() => {
    if (totalProjects === 0) return;
    const maxPage = Math.max(1, Math.ceil(totalProjects / pageSize));
    if (pageIndex > maxPage) {
      setReportPagination(maxPage, pageSize);
    }
  }, [pageIndex, pageSize, totalProjects, setReportPagination]);

  // ★ 分页：按项目分页，再展开为表格行
  const pagedProjects = useMemo(() => {
    const start = (pageIndex - 1) * pageSize;
    return filteredProjects.slice(start, start + pageSize);
  }, [filteredProjects, pageIndex, pageSize]);

  const pagedRows = useMemo(() => buildTableRows(pagedProjects), [pagedProjects]);

  const HEADER_META_COL_SPAN = 3;
  const HEADER_SYNC_COL_SPAN = 3;

  const isHeaderHiddenCol = (record: ReportTableRow) => record.rowType === 'project-header';
  const hideCell = isHeaderHiddenCol;

  const renderProjectHeaderMeta = (record: ReportTableRow) => {
    const brandName = brandLabelMap[record.brand ?? ''] ?? record.brand ?? '—';
    return (
      <span className="report-summary-meta">
        品牌：{brandName} | 模板：{record.templateName || '—'} | 预计入库：{formatDate(record.expectedLaunchDate)}
        {record.creator ? ` | 创建人：${record.creator}` : ''}
      </span>
    );
  };

  const renderProjectHeaderSync = (record: ReportTableRow) => {
    if ((record.projectStatus ?? '').trim().toLowerCase() === 'terminated') {
      return <IpcRichTextView value={record.progressSync} emptyText="—" />;
    }
    const sk = `${record.key}-progressSync`;
    return (
      <EditableTextCell
        value={record.progressSync}
        saving={savingKey === sk}
        multiline
        richVariant="block"
        placeholder="说明整体开发进度及问题…"
        onSave={v => saveCell(record, 'progressSync', v, record.progressSync ?? '')}
      />
    );
  };

  const saveCell = useCallback(async (
    record: ReportTableRow,
    field: string,
    value: string,
    originalValue?: string,
  ) => {
    const isRichField = field === 'note' || field === 'progressSync';
    const nextValue = isRichField
      ? normalizeRichTextForSave(value)
      : (value ?? '');
    const prevValue = isRichField
      ? normalizeRichTextForSave(originalValue)
      : (originalValue ?? '');
    if (nextValue === prevValue) return;

    if ((record.projectStatus ?? '').trim().toLowerCase() === 'terminated') {
      message.warning('已终止项目不可操作');
      return;
    }
    if ((record.node?.specStatus ?? '').trim().toLowerCase() === 'terminated') {
      message.warning('规格已终止，节点不可修改');
      return;
    }

    const scheduleId = record.scheduleId ?? record.node?.scheduleId;
    if (!scheduleId) {
      message.error('当前项目无排期版本，无法保存');
      return;
    }

    const isProgressSync = field === 'progressSync';
    const node = record.node;
    const key = isProgressSync
      ? `${record.key}-progressSync`
      : `${record.key}-${field}`;
    setSavingKey(key);

    try {
      const saveResult = await dashboardReportApi.save({
        projectId: record.projectId,
        scheduleId,
        specScheduleId: isProgressSync ? 0 : (node?.specScheduleId ?? 0),
        milestoneId: isProgressSync ? 0 : (node?.milestoneId ?? 0),
        field,
        value: nextValue,
      });
      const raw = saveResult as unknown as Record<string, unknown>;
      const newScheduleId = Number(raw.scheduleId ?? raw.ScheduleId ?? 0);
      const newVersion = raw.scheduleVersion ?? raw.ScheduleVersion;
      const newStatus = raw.scheduleStatus ?? raw.ScheduleStatus;
      if (newScheduleId > 0) {
        setResult(prev => {
          if (!prev?.projects) return prev;
          return {
            projects: prev.projects.map(p =>
              p.projectId === record.projectId
                ? {
                    ...p,
                    scheduleId: newScheduleId,
                    scheduleVersion: newVersion != null ? Number(newVersion) : p.scheduleVersion,
                    scheduleStatus: newStatus != null ? String(newStatus) : p.scheduleStatus,
                  }
                : p,
            ),
          };
        });
      }
      message.success('已自动保存');
      await fetchReport(appliedFilters);
    } catch (e: unknown) {
      message.error('自动保存失败: ' + (e instanceof Error ? e.message : ''));
    } finally {
      setSavingKey('');
    }
  }, [fetchReport, appliedFilters]);

  const renderMilestoneLabel = (node: NodeRowData) => node.milestoneName;

  // ★ 批量发布逻辑
  const handleBatchPublish = useCallback(async () => {
    setPublishing(true);
    let successCount = 0;
    let failCount = 0;
    for (const projId of selectedProjectIds) {
      const project = result?.projects?.find(p => p.projectId === projId);
      const scheduleId = project?.scheduleId;
      if (!scheduleId) { failCount++; continue; }
      try {
        await scheduleApi.publish(scheduleId);
        successCount++;
      } catch {
        failCount++;
      }
    }
    setPublishing(false);
    setPublishModalVisible(false);
    setSelectedProjectIds([]);
    if (failCount === 0) {
      message.success(`全部 ${successCount} 个项目已发布`);
    } else {
      message.warning(`${successCount} 个发布成功，${failCount} 个失败`);
    }
    await fetchReport();
  }, [selectedProjectIds, result, fetchReport]);

  // ★ 勾选/取消勾选项目
  const handleSelectProject = useCallback((projId: number, checked: boolean) => {
    if (checked) {
      setSelectedProjectIds(prev => [...prev, projId]);
    } else {
      setSelectedProjectIds(prev => prev.filter(id => id !== projId));
    }
  }, []);

  // ★ 全选/取消全选当前页可发布（草稿）项目；已终止项目不可选
  const currentPageProjectIds = useMemo(() => {
    return pagedProjects
      .filter(p =>
        !!p.scheduleId
        && p.scheduleStatus !== 'published'
        && (p.projectStatus ?? '').trim().toLowerCase() !== 'terminated')
      .map(p => p.projectId);
  }, [pagedProjects]);

  const allPageSelected = currentPageProjectIds.length > 0 && currentPageProjectIds.every(id => selectedProjectIds.includes(id));

  const handleSelectAllPage = useCallback(() => {
    if (allPageSelected) {
      setSelectedProjectIds(prev => prev.filter(id => !currentPageProjectIds.includes(id)));
    } else {
      setSelectedProjectIds(prev => [...new Set([...prev, ...currentPageProjectIds])]);
    }
  }, [allPageSelected, currentPageProjectIds]);

  const isTerminatedReportProject = (record: ReportTableRow) =>
    (record.projectStatus ?? '').trim().toLowerCase() === 'terminated';

  const isTerminatedReportSpec = (record: ReportTableRow) =>
    (record.node?.specStatus ?? '').trim().toLowerCase() === 'terminated';

  /** 项目或规格已终止：节点字段只读 */
  const isNodeFieldReadonly = (record: ReportTableRow) =>
    isTerminatedReportProject(record) || isTerminatedReportSpec(record);

  const columns: ColumnsType<ReportTableRow> = [
    // ★ 勾选列：仅草稿可勾选
    {
      title: (
        <Checkbox
          checked={allPageSelected}
          indeterminate={currentPageProjectIds.some(id => selectedProjectIds.includes(id)) && !allPageSelected}
          onChange={handleSelectAllPage}
          disabled={currentPageProjectIds.length === 0}
        />
      ),
      key: 'selection',
      width: 40,
      onCell: record => (record.rowType === 'project-header' ? {} : { className: 'report-node-selection-cell' }),
      render: (_: unknown, record) => {
        if (record.rowType !== 'project-header') return null;
        const canPublish =
          !!record.scheduleId
          && record.scheduleStatus !== 'published'
          && (record.projectStatus ?? '').trim().toLowerCase() !== 'terminated';
        return (
          <Checkbox
            checked={selectedProjectIds.includes(record.projectId)}
            disabled={!canPublish}
            onChange={e => handleSelectProject(record.projectId, e.target.checked)}
          />
        );
      },
    },
    {
      title: '项目',
      key: 'projectName',
      width: 180,
      onCell: record => {
        if (record.rowType === 'project-header') return { className: 'report-header-project-cell' };
        if (record.rowType === 'node') return { className: 'report-node-project-cell' };
        return {};
      },
      render: (_: unknown, record) => {
        if (record.rowType !== 'project-header') return null;
        return (
          <div className="report-project-header">
            <a
              className="proj-link"
              title={record.projectName}
              onClick={() => navigate(`/schedule/${record.projectId}`)}
            >
              {record.projectName}
            </a>
            <div className="report-project-code">
              <ProjectCodeDisplay codes={record.ipdProjectCodes} fallback={record.projectCode} />
            </div>
            <div className="report-project-tags">
              {record.scheduleStatus === 'published' && (
                <IpcStatusBadge variant="success">已发布 v{record.scheduleVersion}</IpcStatusBadge>
              )}
              {record.scheduleStatus === 'draft' && (
                <IpcStatusBadge variant="warning">草稿 v{record.scheduleVersion}</IpcStatusBadge>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: '规格',
      key: 'specName',
      width: 140,
      onCell: record => {
        if (record.rowType === 'project-header') return { className: 'report-header-spec-cell' };
        if (record.rowType === 'node') return { className: 'report-spec-data-cell' };
        return {};
      },
      render: (_: unknown, record) => {
        if (record.rowType === 'project-header') {
          return (
            <span className="report-spec-count">
              {(record.specCount ?? 0) > 0 ? `${record.specCount} 规格` : '未关联需求SKU'}
            </span>
          );
        }
        if (record.rowType === 'node' && record.node) {
          if (record.node.isMerged) {
            return (
              <div className="report-spec-cell">
                <IpcTag variant="primary" className="report-spec-tag">共用</IpcTag>
                {record.node.coverageLabel ? (
                  <span className="report-spec-coverage" title={record.node.coverageLabel}>
                    {record.node.coverageLabel}
                  </span>
                ) : null}
              </div>
            );
          }
          return (
            <SpecSkuDisplay
              title={record.node.specName}
              skuCodes={record.node.skuCodes}
            />
          );
        }
        return null;
      },
    },
    {
      title: '节点',
      key: 'milestoneName',
      width: 160,
      onCell: record => {
        if (record.rowType === 'project-header') {
          return { colSpan: HEADER_META_COL_SPAN, className: 'report-header-meta-cell' };
        }
        return {};
      },
      render: (_: unknown, record) => {
        if (record.rowType === 'project-header') {
          return renderProjectHeaderMeta(record);
        }
        if (record.rowType === 'node' && record.node) {
          return renderMilestoneLabel(record.node);
        }
        return null;
      },
    },
    // ★ 状态列含异常标注
    {
      title: '状态',
      key: 'milestoneStatus',
      width: 140,
      onCell: record => (hideCell(record) ? { colSpan: 0 } : {}),
      render: (_: unknown, record) => {
        if (record.rowType !== 'node' || !record.node) return null;
        const node = record.node;
        if (isNodeFieldReadonly(record)) {
          return (
            <span className="editable-cell editable-cell--status">
              <IpcNodeStatus progress={resolveReportNodeStatusProgress(node.milestoneStatus)} compact />
            </span>
          );
        }
        const sk = `${record.key}-status`;
        return (
          <EditableStatusCell
            value={node.milestoneStatus}
            saving={savingKey === sk}
            anomaly={node.anomaly}
            excludeCompleted={autoCompletionNodeIds.has(Number(node.milestoneId))}
            onSave={v => saveCell(record, 'status', v, node.milestoneStatus)}
          />
        );
      },
    },
    {
      title: '计划开始',
      key: 'planStartDate',
      width: 110,
      onCell: record => (hideCell(record) ? { colSpan: 0 } : {}),
      render: (_: unknown, record) => {
        if (record.rowType !== 'node' || !record.node) return null;
        if (isNodeFieldReadonly(record)) {
          return formatDate(record.node.planStartDate);
        }
        const sk = `${record.key}-planStartDate`;
        return (
          <EditableDateCell
            value={record.node.planStartDate}
            saving={savingKey === sk}
            onSave={v => saveCell(record, 'planStartDate', v, record.node!.planStartDate ? dayjs(record.node!.planStartDate).format('YYYY-MM-DD') : '')}
          />
        );
      },
    },
    {
      title: '计划结束',
      key: 'planEndDate',
      width: 110,
      onCell: record => {
        if (record.rowType === 'project-header') {
          return { colSpan: HEADER_SYNC_COL_SPAN, className: 'report-header-sync-cell' };
        }
        return {};
      },
      render: (_: unknown, record) => {
        if (record.rowType === 'project-header') {
          return renderProjectHeaderSync(record);
        }
        if (record.rowType !== 'node' || !record.node) return null;
        if (isNodeFieldReadonly(record)) {
          return formatDate(record.node.planEndDate);
        }
        const sk = `${record.key}-planEndDate`;
        return (
          <EditableDateCell
            value={record.node.planEndDate}
            saving={savingKey === sk}
            onSave={v => saveCell(record, 'planEndDate', v, record.node!.planEndDate ? dayjs(record.node!.planEndDate).format('YYYY-MM-DD') : '')}
          />
        );
      },
    },
    {
      title: '实际开始',
      key: 'actualStartDate',
      width: 110,
      onCell: record => (hideCell(record) ? { colSpan: 0 } : {}),
      render: (_: unknown, record) => {
        if (record.rowType !== 'node' || !record.node) return null;
        if (isNodeFieldReadonly(record)) {
          return formatDate(record.node.actualStartDate);
        }
        const sk = `${record.key}-actualStartDate`;
        return (
          <EditableDateCell
            value={record.node.actualStartDate}
            saving={savingKey === sk}
            onSave={v => saveCell(record, 'actualStartDate', v, record.node!.actualStartDate ? dayjs(record.node!.actualStartDate).format('YYYY-MM-DD') : '')}
          />
        );
      },
    },
    {
      title: '实际结束',
      key: 'actualEndDate',
      width: 110,
      onCell: record => (hideCell(record) ? { colSpan: 0 } : {}),
      render: (_: unknown, record) => {
        if (record.rowType !== 'node' || !record.node) return null;
        if (isNodeFieldReadonly(record)) {
          return formatDate(record.node.actualEndDate);
        }
        const sk = `${record.key}-actualEndDate`;
        return (
          <EditableDateCell
            value={record.node.actualEndDate}
            saving={savingKey === sk}
            onSave={v => saveCell(record, 'actualEndDate', v, record.node!.actualEndDate ? dayjs(record.node!.actualEndDate).format('YYYY-MM-DD') : '')}
          />
        );
      },
    },
    {
      title: '备注',
      key: 'note',
      width: 220,
      onCell: record => {
        if (record.rowType === 'project-header') return { className: 'report-header-note-cell' };
        return {};
      },
      render: (_: unknown, record) => {
        if (record.rowType === 'project-header') return null;
        if (record.rowType === 'node' && record.node) {
          if (isNodeFieldReadonly(record)) {
            return <IpcRichTextView value={record.node.note} emptyText="—" />;
          }
          const sk = `${record.key}-note`;
          return (
            <EditableTextCell
              value={record.node.note}
              saving={savingKey === sk}
              multiline
              richVariant="inline"
              placeholder="添加备注…"
              onSave={v => saveCell(record, 'note', v, record.node!.note ?? '')}
            />
          );
        }
        return null;
      },
    },
  ];

  return (
    <div className="dashboard-report ipc-section-stack">
      <IpcFilterToolbar
        onReset={resetReportFiltersToDefault}
        onSubmit={applyReportFilters}
        chipItems={reportFilterChips}
        onRemoveChip={removeReportAppliedChip}
        onClearChips={clearAllReportFilters}
        extra={
          <>
            <span className="report-batch-count">已选中 {selectedProjectIds.length} 个项目</span>
            <IpcButton
              variant="primary"
              disabled={selectedProjectIds.length === 0}
              onClick={() => setPublishModalVisible(true)}
              loading={publishing}
            >
              一键发布
            </IpcButton>
            {selectedProjectIds.length > 0 && (
              <IpcButton variant="secondary" onClick={() => setSelectedProjectIds([])}>
                取消选择
              </IpcButton>
            )}
          </>
        }
      >
        <Input
          placeholder="搜索项目名称..."
          value={draftFilters.keyword}
          onChange={e => setDraftFilters(prev => ({ ...prev, keyword: e.target.value }))}
          onPressEnter={applyReportFilters}
          allowClear
          className="ipc-filter-bar__search"
        />
        <Select
          mode="multiple"
          placeholder="全部品牌"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={draftFilters.brands}
          onChange={v => fetchReportIfApplied(patchAndApplyReport({ brands: v ?? [] }))}
          options={brandOptions}
          className="ipc-filter-bar__field"
        />
        <Select
          mode="multiple"
          placeholder="全部模板"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={draftFilters.templateIds}
          onChange={v => fetchReportIfApplied(patchAndApplyReport({ templateIds: v ?? [] }))}
          options={templateOptions}
          className="ipc-filter-bar__field"
        />
        <Select
          mode="multiple"
          placeholder="全部创建人"
          allowClear
          showSearch
          optionFilterProp="label"
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={filterCreators}
          onChange={v => {
            patchAndApplyReport({ creators: v ?? [] });
          }}
          options={creatorOptions}
          className="ipc-filter-bar__field"
        />
        <div className="report-plan-end-range ipc-filter-bar__field ipc-filter-bar__field--range">
          <IpcDateRangePicker
            variant="filter"
            className="ipc-date-range--filter"
            value={[
              filterPlanEndStart?.format('YYYY-MM-DD'),
              filterPlanEndEnd?.format('YYYY-MM-DD'),
            ]}
            onChange={([s, e]) => {
              patchAndApplyReport({
                planEndStart: s || null,
                planEndEnd: e || null,
              });
            }}
            placeholder={['计划结束开始', '计划结束结束']}
          />
        </div>
        <Select
          mode="multiple"
          placeholder="项目状态"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={draftFilters.projectStatuses}
          onChange={v => fetchReportIfApplied(patchAndApplyReport({ projectStatuses: v ?? [] }))}
          options={projectStatusOptions()}
          className="ipc-filter-bar__field"
        />
        <Select
          mode="multiple"
          placeholder="节点状态"
          allowClear
          {...IPC_FILTER_MULTI_SELECT_PROPS}
          value={draftFilters.milestoneStatuses}
          onChange={v => fetchReportIfApplied(patchAndApplyReport({ milestoneStatuses: v ?? [] }))}
          options={milestoneStatusOptions()}
          className="ipc-filter-bar__field"
        />
      </IpcFilterToolbar>

      <IpcTable<ReportTableRow>
        listViewKey={LIST_VIEW_KEYS.dashboardReport}
        panelClassName="dashboard-report-table-region"
        scrollClassName="dashboard-report-table-scroll"
        size="small"
        sortable={false}
        columns={columns}
        dataSource={pagedRows}
        loading={loading}
        rowKey="key"
        autoHeight
        autoHeightDeps={[pagedRows.length, pageIndex, pageSize, loading]}
        scroll={{ x: 1400 }}
        tableLayout="fixed"
        rowClassName={(record, index) => {
          const classes = ['report-row'];
          if (record.rowType === 'project-header') classes.push('report-row--header');
          if (record.rowType === 'node') classes.push('report-row--node');
          if (record.rowType === 'node' && record.node?.anomaly) classes.push('anomaly-highlight');
          const next = pagedRows[index + 1];
          if (!next || next.projectId !== record.projectId) {
            classes.push('report-row--group-end');
          }
          return classes.join(' ');
        }}
        pagination={totalProjects > 0 ? {
            className: 'dashboard-report-pagination',
            current: pageIndex,
            pageSize,
            total: totalProjects,
            pageSizeOptions: [20, 50, 100],
            onChange: (page, size) => {
              setReportPagination(page, size);
            },
          } : false}
        resizable
      />

      <IpcModal
        title="确认一键发布"
        open={publishModalVisible}
        okText="确认发布"
        cancelText="取消"
        confirmLoading={publishing}
        onOk={handleBatchPublish}
        onCancel={() => setPublishModalVisible(false)}
      >
        <p>将发布以下 {selectedProjectIds.length} 个项目的排期版本：</p>
        <ul>
          {selectedProjectIds.map(id => {
            const proj = result?.projects?.find(p => p.projectId === id);
            return <li key={id}>{proj?.projectName ?? `项目 #${id}`}</li>;
          })}
        </ul>
        <p style={{ color: '#FF3B30' }}>发布后排期版本将锁定，请确认所有数据无误。</p>
      </IpcModal>
    </div>
  );
};

export default DashboardReport;
